import type { WorkspaceApp } from './app-shares';

/**
 * The pure id→app rule behind the `/app/<appId>` share link: the app to OPEN, or undefined when the
 * link lands on the Apps screen and stops there.
 *
 * <p>Mirrors iOS `AppRouter.resolvedAppToOpen` and Android `Navigator.resolvedAppToOpen` 1:1 — same
 * three nil cases, same precedence. The link is minted when an app is shared with somebody who may
 * not have a Symphony account yet, so it can name an app this reader owns, one they have only been
 * OFFERED, or one they have nothing to do with.
 *
 * <p>Undefined covers three different facts that all deserve the same behaviour — an unknown id, an id
 * that is only an unanswered OFFER, and a matched app with no openable url (the card lists it but its
 * body is not a control either). In all three the Apps screen already shows the reader whatever truth
 * there is, which is why the caller lands there FIRST and resolves after.
 *
 * <p><b>An offer wins over a match anywhere else.</b> An app still sitting in the offers list has not
 * been accepted, and opening it would skip the one decision the link exists to ask for — and would
 * skip the accept that credits the person who shared it.
 *
 * <p>Owned apps are matched before shared ones purely for determinism; an id in both is the same app.
 */
export function resolvedAppToOpen(
  appId: string | undefined,
  owned: readonly WorkspaceApp[],
  shared: readonly WorkspaceApp[],
  pending: readonly WorkspaceApp[],
): WorkspaceApp | undefined {
  if (!appId) return undefined;
  if (pending.some((app) => app.id === appId)) return undefined;
  const match = [...owned, ...shared].find((app) => app.id === appId);
  return match?.url ? match : undefined;
}

/**
 * The `?open=` value as a single id, or undefined.
 *
 * Next hands a repeated query key back as an array. Taking the first entry rather than refusing keeps
 * a hand-edited `?open=a&open=b` on the same screen it would otherwise land on anyway.
 */
export function openParam(value: string | string[] | undefined): string | undefined {
  const raw = Array.isArray(value) ? value[0] : value;
  const trimmed = raw?.trim();
  return trimmed ? trimmed : undefined;
}

/**
 * Whether every list the id must be resolved against has ANSWERED — a failed read included.
 *
 * <p>Its own function because the trap it guards is documented on the Apps screen and still caught
 * this code: `useAllWorkspaceApps([])` reports <b>ready-with-nothing</b> the instant it is handed an
 * empty id list, which is exactly what it gets while the Spaces are still in flight. So the owned-apps
 * state says "ready, no apps" about an account with eleven of them, and a resolver trusting it opens
 * nothing and silently strips the id — the link appears to do nothing at all, which is the failure it
 * was written to remove. The Spaces read has to be part of the answer.
 *
 * <p>"Answered" deliberately includes `error`: a failed read is a settled one, and waiting forever on
 * it would strand the reader on a screen that never acts on the link they followed.
 */
export function listsHaveAnswered(states: {
  workspaces: string;
  owned: string;
  shared: string;
  pending: string;
}): boolean {
  return Object.values(states).every((status) => status !== 'loading');
}
